import {APIClient} from '../utils/APIClient';
import {API_URL} from '../settings/local_settings';

/**
 * Capa de API de los Informes (app `reports` del backend, perfil de paneles).
 * Endpoints en Dashbo-Backend/reports/urls.py (buscar "client-reports").
 *
 * Errores: el backend contesta tipado (`status` / `error_code`); la rama de error se escribe
 * siempre `status !== 'OK'`, nunca `=== 'ERROR'` — un código tipado no es "ERROR".
 */
const base = (path) => `${API_URL}client-reports/${path}`;

export const InformesApi = {
    // --- plantillas (perfil de paneles) ---
    listTemplates() {
        return new APIClient(base('templates/')).call('GET');
    },
    createTemplate(payload) {
        // payload: { name, layout, description?, is_shared? }
        return new APIClient(base('templates/')).call('POST', payload);
    },
    createTemplateFromColumnsSet(columnsSetId, name) {
        return new APIClient(base('templates/from-columns-set/')).call('POST', {columns_set_id: columnsSetId, name});
    },
    getTemplate(templateId) {
        return new APIClient(base(`templates/${templateId}/`)).call('GET');
    },
    updateTemplate(templateId, payload) {
        return new APIClient(base(`templates/${templateId}/`)).call('PATCH', payload);
    },
    deleteTemplate(templateId) {
        return new APIClient(base(`templates/${templateId}/`)).call('DELETE');
    },
    duplicateTemplate(templateId, name) {
        return new APIClient(base(`templates/${templateId}/duplicate/`)).call('POST', {name});
    },

    // --- informes ---
    /** Todos los informes de los clientes que el usuario puede ver (pestaña «Todos los clientes»). */
    listAll() {
        return new APIClient(base('')).call('GET');
    },
    list(clientId) {
        return new APIClient(base(`?client_id=${encodeURIComponent(clientId)}`)).call('GET');
    },
    create(payload) {
        // payload: { client_id, name, settings, layout? | template_id?, mode?, description? }
        return new APIClient(base('')).call('POST', payload);
    },
    get(informeId) {
        return new APIClient(base(`${informeId}/`)).call('GET');
    },
    update(informeId, payload) {
        return new APIClient(base(`${informeId}/`)).call('PATCH', payload);
    },
    remove(informeId) {
        return new APIClient(base(`${informeId}/`)).call('DELETE');
    },
    applyTemplate(informeId, templateId) {
        return new APIClient(base(`${informeId}/apply-template/`)).call('POST', {template_id: templateId});
    },

    // --- render ---
    renderDraft(clientId, layout, settings) {
        // Lo que hay en el editor, sin guardar.
        return new APIClient(base('render/')).call('POST', {client_id: clientId, layout, settings});
    },
    render(informeId, settingsOverride) {
        return new APIClient(base(`${informeId}/render/`)).call('POST', {settings_override: settingsOverride || null});
    },

    // --- portal ---
    publish(informeId) {
        return new APIClient(base(`${informeId}/publish/`)).call('POST', {});
    },
    unpublish(informeId) {
        return new APIClient(base(`${informeId}/unpublish/`)).call('POST', {});
    },
    reorderPublished(clientId, orderedIds) {
        return new APIClient(base('publish-order/')).call('PATCH', {client_id: clientId, ordered_ids: orderedIds});
    },

    // --- fotos ---
    requestSnapshot(informeId) {
        return new APIClient(base(`${informeId}/snapshot/`)).call('POST', {});
    },
    listSnapshots(informeId) {
        return new APIClient(base(`${informeId}/snapshots/`)).call('GET');
    },
};

// `isOk` vive en `utils/apiResponse`: es el contrato del `APIClient`, no de Informes — lo necesitaba
// cualquiera que guarde por `status`, y estaba acá sólo por dónde nació. Se re-exporta para no tocar
// a sus consumidores, que la importan de este módulo.
export {isOk} from '../utils/apiResponse';

/**
 * La lista que envuelve `OKResponse`: `{status:'OK', data:{<key>:[…]}}`. El shape vive acá y no en
 * cada componente (review 05/09, C-informes-01: dos componentes hacían `r.data || []`).
 */
export function listOf(response, key) {
    const data = response && response.data;
    return data && Array.isArray(data[key]) ? data[key] : [];
}

export function errorOf(response, fallback) {
    if (!response) return {code: 'NETWORK_ERROR', message: fallback};
    return {
        code: response.error_code || response.status || 'ERROR',
        message: response.error_message || response.error || fallback,
        // `APIClient` reconstruye el error a mano y no conserva `panel_id`: sobrevive en raw_body.
        panelId: response.panel_id || (response.raw_body && response.raw_body.panel_id) || null,
        // El `error_message` TAL COMO lo mandó el backend. `message` (arriba) no sirve para
        // decidir: `APIClient` lo arma como `detail || message || error_message || <genérico>`, así
        // que un `detail` de DRF le gana al motivo real y el fallback lo rellena cuando no hay
        // ninguno. Quien necesite el texto del backend —y sólo ése— lee esta clave.
        serverMessage: (response.raw_body && response.raw_body.error_message) || null,
    };
}
